/* eslint-disable no-undef */

import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';

import { GoogleMap, MapAdvancedMarker } from '@angular/google-maps';
import { MatButton, MatIconButton, MatMiniFabButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatCellDef, MatColumnDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
  AuthorizationService,
  ClientSideDataTable,
  Column,
  MapLoaderService,
  Organization,
  OrganizationService,
  Store,
  StoreFilterService,
  StoreService,
  TasksService,
  ThemeService,
  TitleService,
  UserPreference,
  UserService
} from '@ng-cloud/badger-core';
import { FlexDirective, LayoutAlignDirective, LayoutDirective, LayoutGapDirective } from '@ngbracket/ngx-layout';

import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { StoresEditComponent } from '../store/stores/stores-edit/stores-edit.component';

@Component({
  selector: 'badger-portal-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlexDirective,
    GoogleMap,
    LayoutAlignDirective,
    LayoutDirective,
    LayoutGapDirective,
    MapAdvancedMarker,
    MatButton,
    MatCellDef,
    MatColumnDef,
    MatIcon,
    MatIconButton,
    MatMiniFabButton,
    MatRow,
    MatRowDef,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    MatTable,
    NgClass,
    RouterLink,
    StoresEditComponent
  ]
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  static readonly DEFAULT_ZOOM_LEVEL = 10;
  static readonly MAX_ZOOM_LEVEL = 12;

  static readonly DEFAULT_LATITUDE = 37.95108;
  static readonly DEFAULT_LONGITUDE = -84.52324;

  static readonly HOME_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" style="pointer-events: none;" class="marker-pulse">
      <g id="Group_8034" data-name="Group 8034" transform="translate(-230 -108)">
        <circle id="Ellipse_4244" data-name="Ellipse 4244" cx="12" cy="12" r="12" transform="translate(230 108)" fill="#028aff"/>
        <g id="Group_7446" data-name="Group 7446" transform="translate(236 114)">
          <rect id="Rectangle_2439" data-name="Rectangle 2439" width="12" height="11.796" transform="translate(0 0.204)" fill="none"/>
          <path id="Subtraction_16" data-name="Subtraction 16" d="M2.852,10.6H.708A1.7,1.7,0,0,1-1,8.925V3.978A1.664,1.664,0,0,1-.4,2.7L3.517-.6l0,0a1.731,1.731,0,0,1,2.206,0l0,0L9.642,2.7a1.665,1.665,0,0,1,.6,1.282V8.925A1.7,1.7,0,0,1,8.537,10.6H6.394a.585.585,0,0,1-.59-.58V5.684a.577.577,0,0,0-.074-.3H3.516a.577.577,0,0,0-.074.3v4.34A.586.586,0,0,1,2.852,10.6ZM4.284.281.371,3.578l0,0a.514.514,0,0,0-.187.4V8.925a.524.524,0,0,0,.528.519H2.262V5.684a1.33,1.33,0,0,1,1.18-1.462H5.8a1.33,1.33,0,0,1,1.18,1.462v3.76H8.537a.524.524,0,0,0,.528-.519V3.978a.515.515,0,0,0-.187-.4l0,0L4.962.282a.535.535,0,0,0-.678,0Z" transform="translate(1.088 1)" fill="#fff"/>
        </g>
      </g>
    </svg>`;

  static readonly STORE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" style="pointer-events: none;" class="marker-pulse">
      <g id="Ellipse_14" data-name="Ellipse 14" transform="translate(4 4)" fill="#028aff" stroke="rgba(2,138,255,0.31)" stroke-width="4">
        <circle cx="8" cy="8" r="8" stroke="none"/>
        <circle cx="8" cy="8" r="10" fill="none"/>
      </g>
    </svg>`;

  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild(MatSidenav) sidenav!: MatSidenav;

  homeStore = signal<Store>(undefined);
  loaded = signal<boolean>(false);
  hasMultiStoreAccess = signal<boolean>(false);

  isFittingBounds = false;

  subscriptions: Subscription[] = [];

  organizations = new Map<number, Organization>();
  stores = new Map<number, Store>();

  selectedStore: Store;
  userPreferences: UserPreference;

  dataTable: ClientSideDataTable<Store>;

  pinnedStoreIds: number[] = [];
  pinnedStores: Store[] = [];
  homeStorePin: HTMLElement;
  storePin: HTMLElement;
  pinnedStoreCount = signal<number>(0);
  pinnedLoaded = signal<boolean>(false);

  showUserMessage = computed<boolean>(
    () => this.loaded() && !this.homeStore() && !this.hasMultiStoreAccess()
  );

  showMultiMessage = computed<boolean>(
    () =>
      this.loaded() &&
      this.pinnedLoaded() &&
      this.hasMultiStoreAccess() &&
      this.pinnedStoreCount() === 0
  );

  storeMarkers = signal<StoreMarker[]>([]);
  isDarkMode = signal<boolean>(true);

  isMapLoaded = signal<boolean>(false);
  themeLoaded = signal<boolean>(false);

  displayMap = computed<boolean>(
    () => this.isMapLoaded() && this.themeLoaded()
  );

  zoom = signal<number>(HomeComponent.DEFAULT_ZOOM_LEVEL);
  initialLatitude = signal<number>(HomeComponent.DEFAULT_LATITUDE);
  initialLongitude = signal<number>(HomeComponent.DEFAULT_LONGITUDE);

  mapOptions = computed<google.maps.MapOptions>(() => ({
    mapId: 'ed485ed3483551b5e1529b5e',
    center: { lat: this.initialLatitude(), lng: this.initialLongitude() },
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    colorScheme: this.isDarkMode() ? 'DARK' : 'LIGHT',
    zoomControl: false,
    cameraControl: false,
    keyboardShortcuts: false
  }));

  constructor(
    protected authService: AuthorizationService,
    protected changeDetector: ChangeDetectorRef,
    protected mapLoaderService: MapLoaderService,
    protected orgService: OrganizationService,
    protected router: Router,
    protected storeFilterService: StoreFilterService,
    protected storeService: StoreService,
    protected taskService: TasksService,
    protected themeService: ThemeService,
    protected titleService: TitleService,
    protected userService: UserService
  ) {
    authService
      .accessToAny([
        'admin',
        'Organization Executive',
        'Organization Admin',
        'IT',
        'Regional Manager'
      ])
      .subscribe((hasAccess) => {
        this.hasMultiStoreAccess.set(hasAccess);
        this.createStoreTable();
      });

    this.getUserLocation();
  }

  ngOnInit() {
    this.titleService.setTitle('Home');

    this.mapLoaderService
      .load()
      .then(() => {
        this.isMapLoaded.set(true);
      })
      .catch((err) => {
        console.error('Failed to load Google Maps:', err);
      });
  }

  ngAfterViewInit(): void {
    const parser = new DOMParser();

    this.homeStorePin = parser.parseFromString(
      HomeComponent.HOME_SVG,
      'image/svg+xml'
    ).documentElement;
    this.storePin = parser.parseFromString(
      HomeComponent.STORE_SVG,
      'image/svg+xml'
    ).documentElement;

    this.subscriptions.push(
      this.themeService.themes().subscribe((theme) => this.updateTheme(theme))
    );

    this.authService.getCurrentUser().subscribe((authUser) => {
      this.userService.getUser(authUser.id).subscribe((currentUser) => {
        if (currentUser.homeStoreId) {
          this.userService.setCurrentUserHomeStoreId(currentUser.homeStoreId);
          this.storeService
            .getStore(currentUser.homeStoreId)
            .subscribe((store) => {
              this.homeStore.set(store);
              this.updatePinnedStores();
              this.stores.set(this.homeStore().id, this.homeStore());
              this.orgService.addOrganization(this.homeStore()).subscribe();
              this.loaded.set(true);

              this.selectStore(this.homeStore());

              this.taskService
                .getStats(currentUser.homeStoreId)
                .subscribe((stats) => {
                  this.homeStore().activeTaskCount = stats['total_tasks_active'];
                });

              this.getStatsForOrg(currentUser.organizationId);
            });
        }
        else {
          this.loaded.set(true);
        }

        this.storeFilterService.getStoreFilter().subscribe((result) => {
          this.selectedStore = result;
          this.zoomToStore(result);
          this.applySelectedMarkerClass();
        });

        if (this.hasMultiStoreAccess) {
          this.userService
            .getOrCreateUserPreference(currentUser)
            .subscribe((userPrefs) => {
              this.userPreferences = userPrefs;
              if (userPrefs.preferences['pinned_stores']) {
                this.updatePinnedStoreIds(
                  userPrefs.preferences['pinned_stores']
                );
              }
            });

          this.userService
            .userPreferences(currentUser.id)
            .subscribe((response) => {
              if (
                response.preferences &&
                response.preferences['pinned_stores']
              ) {
                const newPinnedStores = response.preferences['pinned_stores'];

                if (_.xor(newPinnedStores, this.pinnedStoreIds).length !== 0) {
                  this.userPreferences.preferences['pinned_stores'] =
                    response.preferences['pinned_stores'];
                  this.updatePinnedStoreIds(
                    response.preferences['pinned_stores']
                  );
                }
              }
            });
        }
      });
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => event.url === '/home')
      )
      .subscribe(() => {
        this.updateTheme(this.themeService.getCurrentTheme());
        this.updateMarkers();
      });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

  onMapInitialized() {
    this.changeDetector.detectChanges();
    this.fitMapToMarkers();
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          if (position) {
            this.initialLatitude.set(position.coords.latitude);
            this.initialLongitude.set(position.coords.longitude);
          }
        },
        (error: GeolocationPositionError) => {
          console.error('Unable to obtain user location: ', error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
    else {
      console.error('Geolocation is not supported by this browser.');
    }
  }

  updateTheme(theme: string) {
    this.themeLoaded.set(false);
    this.isDarkMode.set(this.themeService.isDarkTheme(theme));

    setTimeout(() => {
      this.themeLoaded.set(true);
    }, 0);
  }

  fitMapToMarkers(): void {
    if (!this.googleMap?.googleMap) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    // Include home store marker
    if (this.homeStore()?.hasLocationData()) {
      bounds.extend(
        new google.maps.LatLng(
          this.homeStore().latitude,
          this.homeStore().longitude
        )
      );
    }

    this.storeMarkers().forEach((marker) => {
      bounds.extend(
        new google.maps.LatLng(marker.store.latitude, marker.store.longitude)
      );
    });

    if (!bounds.isEmpty()) {
      this.isFittingBounds = true;
      this.googleMap.fitBounds(bounds);
    }

    this.changeDetector.detectChanges();
  }

  selectStore(store: Store) {
    this.storeFilterService.setStoreFilter(store);
  }

  zoomToStore(store: Store) {
    if (this.googleMap?.googleMap && store?.hasLocationData()) {
      const location = new google.maps.LatLng(store.latitude, store.longitude);

      this.googleMap.panTo(location);
      //this.zoom.set(HomeComponent.MAX_ZOOM_LEVEL);
    }
  }

  onMarkerInit(marker: google.maps.marker.AdvancedMarkerElement, store: Store) {
    marker.addEventListener('gmp-click', () => {
      this.selectStore(store);
    });
  }

  onBoundsChanged() {
    if (!this.isFittingBounds) {
      return;
    }

    const currentZoom = this.googleMap.getZoom();

    if (currentZoom === undefined) {
      return;
    }

    if (currentZoom > HomeComponent.MAX_ZOOM_LEVEL) {
      this.googleMap.googleMap?.setZoom(HomeComponent.MAX_ZOOM_LEVEL);
    }

    this.isFittingBounds = false;
  }

  createStoreTable() {
    const columnDefs: Column[] = [];
    columnDefs.push(new Column('name'));
    this.dataTable = new ClientSideDataTable<Store>(columnDefs);
  }

  updatePinnedStoreIds(storeIds: number[]) {
    this.pinnedStoreIds = [...storeIds];
    this.pinnedStoreCount.set(this.pinnedStoreIds.length);
    this.pinnedLoaded.set(true);

    const storesToFetch = _.difference(storeIds, [...this.stores.keys()]);

    if (storesToFetch.length > 0) {
      this.storeService
        .getStores({ 'id[]': storesToFetch })
        .subscribe((stores) => {
          stores.forEach((store) => {
            this.stores.set(store.id, store);
            this.orgService.addOrganization(store).subscribe((organization) => {
              this.taskService.getStats(store.id).subscribe((stats) => {
                store.activeTaskCount = stats['total_tasks_active'];
                this.getStatsForOrg(organization.id);
              });
            });
          });

          this.updatePinnedStores();
        });
    }
    else {
      this.updatePinnedStores();
    }
  }

  updatePinnedStores() {
    this.pinnedStores.length = 0;

    this.updateMarkers();

    this.sortPinnedStores();
    this.dataTable.data = _.filter(
      this.pinnedStores,
      (pinnedStore) => pinnedStore.id !== this.homeStore()?.id
    );

    this.fitMapToMarkers();
  }

  updateMarkers() {
    const markers: StoreMarker[] = [];

    if (this.homeStore()?.hasLocationData()) {
      markers.push(
        new StoreMarker(
          this.homeStore(),
          this.homeStorePin.cloneNode(true) as HTMLElement,
          10
        )
      );
    }

    _.forEach(this.pinnedStoreIds, (storeId) => {
      const store = this.stores.get(storeId);

      if (store) {
        this.pinnedStores.push(store);
        store.hasLocationData() &&
        markers.push(
          new StoreMarker(
            store,
            this.storePin.cloneNode(true) as HTMLElement
          )
        );
      }
    });

    this.storeMarkers.set(markers);
    this.applySelectedMarkerClass();
  }

  private applySelectedMarkerClass() {
    const selectedId = this.selectedStore?.id;
    this.storeMarkers().forEach((marker) => {
      marker.pinElement.classList.toggle('selectedMarker', marker.store.id === selectedId);
    });
  }

  sortPinnedStores() {
    const data = this.dataTable.data.slice();

    this.dataTable.data = data.sort((a, b) =>
      a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
    );
  }

  getStatsForOrg(organizationId: number) {
    if (!this.organizations.has(organizationId)) {
      this.orgService
        .getOrganization(organizationId)
        .subscribe((organization) => {
          this.organizations.set(organization.id, organization);
          this.subscriptions.push(
            this.taskService
              .getOrgTaskStats(organization.guid)
              .subscribe((taskStats) => {
                this.updateTaskCounts(taskStats);
              })
          );
        });
    }
  }

  updateTaskCounts(taskStats) {
    _.forOwn(taskStats['active_by_store'], (taskCount, storeIdString) => {
      const storeId = +storeIdString;

      if (this.homeStore()?.id === storeId) {
        this.homeStore().activeTaskCount = taskCount;
      }

      const store = _.find(this.pinnedStores, { id: storeId });
      store && (store.activeTaskCount = taskCount);
    });
  }

  protected closeSidenavCheck(event: PointerEvent) {
    const target = event.target as HTMLElement;

    if (!target.closest('button')) {
      this.sidenav && this.sidenav.opened && this.sidenav.close();
    }
  }
}

class StoreMarker {
  public store: Store;
  public pinElement: HTMLElement;
  public zIndex: number;

  constructor(store: Store, pinElement: HTMLElement, zIndex: number = 1) {
    this.store = store;
    this.pinElement = pinElement.cloneNode(true) as HTMLElement;
    this.zIndex = zIndex;
  }
}
